import {Dispatch, SetStateAction} from 'react';
import {safeConsoleError} from './sanitize';
import {parseApiError, ParsedApiError} from './error-handler';

/**
 * Global error handler - displays error to user and tracks in analytics
 * Uses parseApiError as single source of truth for message resolution
 *
 * @param error - The error to handle
 * @param setError - Optional callback to set error string in component state
 * @param context - Optional context string describing the operation that failed (e.g., "Failed to load rosters")
 * @returns ParsedApiError object with structured error information (message, errorId, retryable, etc.)
 *
 * @example
 * // Traditional usage (maintains backwards compatibility)
 * handleError(error, setError);
 *
 * @example
 * // With context for better logging
 * handleError(error, setError, 'Failed to load provider rosters');
 *
 * @example
 * // New usage - access parsed error for retry logic
 * const parsedError = handleError(error, setError, 'Failed to save changes');
 * if (parsedError.retryable) {
 *     // Show retry button
 * }
 */
export default function handleError(
    error: Error,
    setError: Dispatch<SetStateAction<string>> | ((s: string) => void) | null,
    context?: string
): ParsedApiError {
    const parsed = parseApiError(error);

    if (parsed.isAbortError) {
        return parsed; // Silent abort - user cancelled
    }

    // Enhanced logging with context and structured error details
    const logContext = context || 'Error occurred';
    const errorDetails = [
        `${logContext}:`,
        error.message
    ];

    // Add status code if available
    if (parsed.status) {
        errorDetails.push(`[Status: ${parsed.status}]`);
    }

    // Add error ID if available (exclude generic client-side IDs)
    if (parsed.errorId && parsed.errorId !== 'network-error' && parsed.errorId !== 'abort') {
        errorDetails.push(`[ID: ${parsed.errorId}]`);
    }

    // Add retryability flag
    errorDetails.push(`[Retryable: ${parsed.retryable}]`);

    // Add field if it's a validation error
    if (parsed.field) {
        errorDetails.push(`[Field: ${parsed.field}]`);
    }

    // Log the structured error message
    safeConsoleError(...errorDetails);

    // Log stack trace for debugging
    if (error.stack) {
        safeConsoleError(error.stack);
    }

    // Build user-facing message with error ID if present (exclude known client-side IDs)
    const message = parsed.errorId && parsed.errorId !== 'network-error' && parsed.errorId !== 'abort'
        ? `${parsed.message} (Error ID: ${parsed.errorId})`
        : parsed.message;

    setError && setError(message);

    return parsed;
}